import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, LogIn, LogOut, Square, Download } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { Card, Btn, inp } from "@/components/ui-kit";
import { useRealtime } from "@/hooks/useRealtime";

const dur = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
const hm = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

export function ClockButton() {
  const { restaurant } = useAuth();
  const { t } = useT();
  const rid = restaurant?.id;
  const [shift, setShift] = useState(null);
  const load = useCallback(() => rid && api.get(`/restaurants/${rid}/shifts/me`).then((r) => setShift(r.data)), [rid]);
  useEffect(() => { load(); }, [load]);
  useRealtime(rid, load, ["shifts"]);
  const toggle = async () => {
    try { await api.post(`/restaurants/${rid}/shifts/${shift ? "clock-out" : "clock-in"}`); toast.success(shift ? t("shift_ended") : t("shift_started")); load(); } catch (ex) { toast.error(errMsg(ex)); }
  };
  if (!rid) return null;
  return (
    <button data-testid="clock-btn" onClick={toggle} className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${shift ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
      {shift ? <><span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /><span className="hidden sm:inline">{t("on_shift")} {hm(shift.start)} ·</span> <LogOut size={12} />{t("end_shift")}</> : <><LogIn size={14} /><span className="hidden sm:inline">{t("start_shift")}</span></>}
    </button>
  );
}

export default function Shifts() {
  const { restaurant, user } = useAuth();
  const { t } = useT();
  const rid = restaurant.id;
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [list, setList] = useState([]);
  const load = useCallback(() => api.get(`/restaurants/${rid}/shifts`, { params: { date } }).then((r) => setList(r.data)), [rid, date]);
  useEffect(() => { load(); }, [load]);
  useRealtime(rid, load, ["shifts"]);
  const onShift = list.filter((s) => !s.end);
  const closeShift = async (s) => { try { await api.patch(`/restaurants/${rid}/shifts/${s.id}/close`); load(); } catch (ex) { toast.error(errMsg(ex)); } };
  const totals = list.reduce((acc, s) => { acc[s.user_name] = (acc[s.user_name] || 0) + s.minutes; return acc; }, {});
  const [busyPdf, setBusyPdf] = useState(false);
  const monthPdf = async () => {
    setBusyPdf(true);
    const month = date.slice(0, 7);
    try {
      const res = await api.get(`/restaurants/${rid}/shifts/monthly.pdf`, { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `ore_lavorate_${month}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusyPdf(false); }
  };
  return (
    <Card className="mt-6" data-testid="shifts-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-heading text-lg font-bold"><Clock size={18} className="text-[#4a8a56]" />{t("shifts")}</p>
        <div className="flex items-center gap-2">
          <input data-testid="shifts-date-input" type="date" className={inp + " w-auto"} value={date} onChange={(e) => setDate(e.target.value)} />
          {user.role === "owner" && <Btn data-testid="shifts-month-pdf-btn" variant="outline" disabled={busyPdf} onClick={monthPdf}><Download size={16} />{t("month_hours_pdf")}</Btn>}
        </div>
      </div>
      <div className="mb-4 flex flex-wrap gap-2">
        <span className="text-xs font-semibold uppercase text-slate-500">{t("on_shift_now")}:</span>
        {onShift.length === 0 && <span className="text-xs text-slate-400">—</span>}
        {onShift.map((s) => <span key={s.id} data-testid={`on-shift-${s.user_name}`} className="flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800"><span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />{s.user_name} · {hm(s.start)}</span>)}
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="py-2">{t("name")}</th><th>{t("role")}</th><th>{t("shift_start")}</th><th>{t("shift_end")}</th><th className="text-right">{t("duration")}</th>{user.role === "owner" && <th />}</tr></thead>
        <tbody className="divide-y divide-slate-100">
          {list.map((s) => <tr key={s.id} data-testid={`shift-row-${s.id}`}>
            <td className="py-2 font-semibold">{s.user_name}</td><td className="text-slate-600">{t(s.staff_role)}</td><td>{hm(s.start)}</td>
            <td>{s.end ? hm(s.end) : <span className="rounded-full bg-emerald-50 px-2 text-xs font-bold text-emerald-700">{t("on_shift")}</span>}</td>
            <td className="text-right font-mono">{dur(s.minutes)}</td>
            {user.role === "owner" && <td className="text-right">{!s.end && <Btn data-testid={`close-shift-${s.id}`} variant="ghost" className="px-2 py-1 text-xs text-rose-600" onClick={() => closeShift(s)}><Square size={12} />{t("close_shift")}</Btn>}</td>}
          </tr>)}
        </tbody>
      </table>
      {list.length === 0 && <p className="py-3 text-sm text-slate-500">{t("no_shifts")}</p>}
      {Object.keys(totals).length > 0 && <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3 text-xs">{Object.entries(totals).map(([n, m]) => <span key={n} className="rounded-full bg-slate-100 px-2.5 py-1"><b>{n}</b> {dur(m)}</span>)}</div>}
    </Card>
  );
}
